// ─── Quello che il circolo dichiara di sé, per il bot dei soci ───────────────
//
// 🧭 Si scrive dalle Impostazioni («Bot dei soci») e il bot lo legge da `/kb`.
// Uno schema solo: valida quello che arriva dalla schermata, e tipizza la
// schermata.
//
// ⛔ **Ogni voce può essere NULLA, e nulla vuol dire «non lo dichiariamo»**: il
// bot in quel caso dice che non riesce a recuperarla, e non mette un valore
// «ragionevole» al suo posto. 📌 *Un valore di ripiego non si distingue da un
// valore vero: è per questo che è pericoloso, non perché sia sbagliato.*
//
// ⚠️ Tutto opzionale, e `catch(null)` su ogni voce: questa riga la scrive una
// persona, e una voce scritta storta non deve far cadere la risposta intera —
// farebbe sparire anche le voci giuste.
import { z } from 'zod';
import { fasciaDiLivelloSchema } from './livelli.js';
const vocePositiva = z.number().positive().nullish().catch(null);
const voceTesto = z.string().trim().min(1).nullish().catch(null);
/**
 * 📚 Gli argomenti della porta «Domande e info sul circolo» che il circolo
 * scrive di suo pugno.
 *
 * 🗣️ Dal documento dei flussi (21/09/2026): *«Una porta sola, a bottoni, con
 * otto argomenti: dove siamo, i nostri campi, cosa trovi al circolo, come si
 * paga, prenotare e disdire, chiedi all'Assistente, chi è LoZio, privacy e
 * dati. Le risposte vengono dal circolo, non sono scritte dentro il bot:
 * quando cambia una regola cambia la risposta.»*
 * ⇒ Qui stanno i **cinque** che solo il circolo sa dire. Gli altri tre li dice
 * il bot di suo: cosa sa fare l'assistente (lo costruisce dal proprio menu),
 * chi l'ha fatto, e la privacy (che ha già la sua voce qui sotto).
 *
 * ⚖️ L'ordine è quello del documento, ed è l'ordine dei bottoni.
 */
export const ARGOMENTI_DEL_CIRCOLO = [
    { chiave: 'dove', titolo: 'Dove siamo', aiuto: 'Indirizzo, come si arriva, dove si parcheggia.' },
    { chiave: 'campi', titolo: 'I nostri campi', aiuto: 'Quanti sono, coperti o scoperti, l’illuminazione.' },
    {
        chiave: 'servizi',
        titolo: 'Cosa trovi al circolo',
        aiuto: 'Spogliatoi, docce, bar, noleggio racchette e palline.',
    },
    {
        chiave: 'pagamenti',
        titolo: 'Come si paga',
        aiuto: 'Contanti, carta, borsellino: come e dove si ricarica.',
    },
    {
        chiave: 'prenotare',
        titolo: 'Prenotare e disdire',
        aiuto: 'Come si prenota e come si disdice, oltre alle regole qui sopra.',
    },
    /**
     * 🏆 25/09/2026, voce 13 del bot: *«per i tornei è solamente un'informativa
     * che sarà data con un numero di telefono da contattare per iscriversi»*. Il
     * bot la mostra in «Domande e info» e la manda, alle 8, a chi l'ha chiesta.
     * Vuota ⇒ il bot dice che non ci sono tornei in programma, e non manda niente.
     */
    {
        chiave: 'tornei',
        titolo: 'Tornei ed eventi',
        aiuto: 'I prossimi tornei ed eventi: date, per chi sono, il numero da chiamare per iscriversi.',
    },
];
/**
 * Un argomento: il testo che legge il socio, e gli appunti di chi lo mantiene.
 *
 * 🔒⭐ **Gli appunti non escono mai verso il bot.** Dal documento: *«nella base
 * di conoscenza convivono testi per il socio e annotazioni di chi la mantiene,
 * e le seconde restano fuori»*. Sono due caselle diverse perché una casella
 * sola, con «nota interna:» scritto dentro, uscirebbe intera il giorno in cui
 * qualcuno dimentica i due punti.
 */
export const argomentoSalvatoSchema = z
    .object({ testo: voceTesto, appunti: voceTesto })
    .partial()
    .nullish()
    .catch(null);
export const conoscenzaSalvataSchema = z.object({
    segreteria: z
        .object({
        telefono: voceTesto,
        orari: voceTesto,
        /**
         * 💬 Il numero su cui la segreteria legge WhatsApp, **col prefisso del
         * paese**. Vuoto = niente bottone WhatsApp nel bot. ⇒ `whatsappInternazionale`.
         */
        whatsapp: voceTesto,
    })
        .partial()
        .nullish()
        .catch(null),
    privacy: z.object({ informativaUrl: voceTesto }).partial().nullish().catch(null),
    prenotabilita: z.object({ giorniDiAnticipoMax: vocePositiva }).partial().nullish().catch(null),
    disdetta: z.object({ oreMinime: vocePositiva }).partial().nullish().catch(null),
    /**
     * 🎾 La scala dei livelli **del circolo**: le parole con cui chiama i suoi
     * numeri, e di quanto due livelli possono distare per giocare insieme.
     * ⇒ `modules/consumer/livelli.ts` per il perché non stanno nel bot.
     */
    livelli: z
        .object({
        fasce: z.array(fasciaDiLivelloSchema).catch([]),
        scartoMassimo: vocePositiva,
    })
        .partial()
        .nullish()
        .catch(null),
    /** 📚 Le informazioni sul circolo, argomento per argomento. */
    informazioni: z
        .object({
        dove: argomentoSalvatoSchema,
        campi: argomentoSalvatoSchema,
        servizi: argomentoSalvatoSchema,
        pagamenti: argomentoSalvatoSchema,
        prenotare: argomentoSalvatoSchema,
        tornei: argomentoSalvatoSchema,
    })
        .partial()
        .nullish()
        .catch(null),
});
/**
 * 💬 Il numero WhatsApp della segreteria nella sola forma che WhatsApp capisce
 * senza indovinare: `+` e le cifre, prefisso del paese compreso
 * (`+393331234567`). `null` se la forma non è quella.
 *
 * 🚨⭐ **Senza prefisso non si indovina.** Il bottone apre `wa.me/<cifre>`, e
 * `wa.me` legge le prime cifre come prefisso del paese: `0422 123456` aprirebbe
 * la chat con uno sconosciuto in un altro paese. Mettere `39` davanti «perché
 * siamo in Italia» è proprio il valore di ripiego che non si distingue da uno
 * vero. ⇒ Si accetta `+39…` o `0039…`, e tutto il resto non esce.
 * 📏 Da 10 a 15 cifre col prefisso: il tetto di E.164, e il minimo di un
 * numero italiano vero.
 *
 * ⚖️ Una funzione sola, e la usano tutt'e due: la schermata per dire prima di
 * salvare che il numero non va, il ponte per non mandarlo al bot.
 */
export function whatsappInternazionale(s) {
    const grezzo = (s ?? '').trim();
    if (!/^(\+|00)\d/.test(grezzo))
        return null;
    // Spazi, trattini, punti e parentesi sono solo modi di scriverlo.
    if (/[^\d\s+().-]/.test(grezzo.slice(1)))
        return null;
    const cifre = grezzo.replace(/^(\+|00)/, '').replace(/\D/g, '');
    return cifre.length >= 10 && cifre.length <= 15 ? `+${cifre}` : null;
}
//# sourceMappingURL=conoscenza.js.map